import React, { useState, useEffect } from "react";
import { Search, Zap, Heart, MessageSquare, Eye, User, Sparkles, Shield, Video, Mic, Type, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { usePortal, PortalMode, IntroType } from "@/hooks/usePortal";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import IntroCapsuleRecorder from "./IntroCapsuleRecorder";

interface CreatePortalModalProps {
  open: boolean;
  onClose: () => void;
  prefilledUserId?: string;
  prefilledUserName?: string;
}

const modes: { 
  id: PortalMode; 
  label: string; 
  description: string; 
  icon: React.ElementType;
  auraColor: string;
  gradient: string;
  bgGradient: string;
}[] = [
  { 
    id: "serious", 
    label: "Serious Dating", 
    description: "Looking for something real", 
    icon: Heart,
    auraColor: "from-blue-600 to-blue-800",
    gradient: "bg-gradient-to-r from-blue-500 to-blue-700",
    bgGradient: "from-blue-50 to-blue-100/50",
  },
  { 
    id: "casual", 
    label: "Fun & Casual", 
    description: "Keep it light and fun", 
    icon: Zap,
    auraColor: "from-pink-400 to-rose-500",
    gradient: "bg-gradient-to-r from-pink-500 to-rose-500",
    bgGradient: "from-pink-50 to-rose-100/50",
  },
  { 
    id: "deep", 
    label: "Deep Talk", 
    description: "Meaningful conversations", 
    icon: MessageSquare,
    auraColor: "from-purple-500 to-violet-600",
    gradient: "bg-gradient-to-r from-purple-500 to-violet-600",
    bgGradient: "from-purple-50 to-violet-100/50",
  },
  { 
    id: "anonymous", 
    label: "Anonymous First", 
    description: "Get to know each other first", 
    icon: Eye,
    auraColor: "from-slate-500 to-gray-600",
    gradient: "bg-gradient-to-r from-slate-500 to-gray-600",
    bgGradient: "from-slate-50 to-gray-100/50",
  },
];

interface SearchResult {
  id: string;
  name: string;
  photo_url: string | null;
}

const CreatePortalModal = ({ open, onClose, prefilledUserId, prefilledUserName }: CreatePortalModalProps) => {
  const { user } = useAuthContext();
  const { sendInvite } = usePortal();
  
  const [step, setStep] = useState<"select" | "mode" | "intro" | "message">(prefilledUserId ? "mode" : "select");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SearchResult | null>(
    prefilledUserId ? { id: prefilledUserId, name: prefilledUserName || "", photo_url: null } : null
  );
  const [selectedMode, setSelectedMode] = useState<PortalMode>("casual");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [suggestedMatches, setSuggestedMatches] = useState<SearchResult[]>([]);
  
  // Intro capsule state
  const [introCapsule, setIntroCapsule] = useState<{
    type: IntroType;
    content: Blob | string;
    duration?: number;
  } | null>(null);

  // Fetch recent matches for suggestions
  useEffect(() => {
    const fetchSuggestedMatches = async () => {
      if (!user?.id || !open) return;
      
      const { data } = await supabase
        .from("matches")
        .select(`
          id,
          user1_id,
          user2_id,
          user1:profiles!matches_user1_id_fkey(id, name, photo_url),
          user2:profiles!matches_user2_id_fkey(id, name, photo_url)
        `)
        .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
        .order("matched_at", { ascending: false })
        .limit(3);

      if (data) {
        const matches = data.map((match: any) => {
          const otherUser = match.user1_id === user.id ? match.user2 : match.user1;
          return {
            id: otherUser?.id || "",
            name: otherUser?.name || "Unknown",
            photo_url: otherUser?.photo_url || null,
          };
        }).filter((m: SearchResult) => m.id);
        setSuggestedMatches(matches);
      }
    };

    fetchSuggestedMatches();
  }, [user?.id, open]);

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    const { data } = await supabase
      .from("profiles")
      .select("id, name, photo_url")
      .neq("id", user?.id || "")
      .ilike("name", `%${query}%`)
      .limit(10);

    setSearchResults(data || []);
    setSearching(false);
  };

  const handleSelectUser = (selectedUserData: SearchResult) => {
    setSelectedUser(selectedUserData);
    setStep("mode");
  };

  const handleSelectMode = (mode: PortalMode) => {
    setSelectedMode(mode);
    setStep("intro");
  };

  const handleIntroReady = (type: IntroType, content: Blob | string, duration?: number) => {
    setIntroCapsule({ type, content, duration });
  };

  const handleIntroClear = () => {
    setIntroCapsule(null);
  };

  const handleSkipIntro = () => {
    setStep("message");
  };

  const handleContinueWithIntro = () => {
    setStep("message");
  };

  const handleSend = async () => {
    if (!selectedUser) return;

    setSending(true);
    const success = await sendInvite(
      selectedUser.id, 
      selectedMode, 
      message || undefined,
      introCapsule ? { type: introCapsule.type, content: introCapsule.content } : null
    );
    setSending(false);

    if (success) {
      onClose();
      // Reset state
      setStep(prefilledUserId ? "mode" : "select");
      setSearchQuery("");
      setSearchResults([]);
      setSelectedUser(prefilledUserId ? { id: prefilledUserId, name: prefilledUserName || "", photo_url: null } : null);
      setSelectedMode("casual");
      setMessage("");
      setIntroCapsule(null);
    }
  };

  const handleBack = () => {
    if (step === "message") setStep("intro");
    else if (step === "intro") setStep("mode");
    else if (step === "mode") {
      if (prefilledUserId) {
        onClose();
      } else {
        setStep("select");
      }
    }
  };

  const getIntroTypeIcon = () => {
    if (!introCapsule) return null;
    switch (introCapsule.type) {
      case "video": return <Video className="w-4 h-4 text-rose-500" />;
      case "voice": return <Mic className="w-4 h-4 text-violet-500" />;
      case "text": return <Type className="w-4 h-4 text-blue-500" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            {step === "select" && "Open a Portal"}
            {step === "mode" && "Choose Portal Mode"}
            {step === "intro" && "Add an Intro (Optional)"}
            {step === "message" && "Add a Message"}
          </DialogTitle>
          {step === "select" && (
            <p className="text-sm text-muted-foreground mt-1">
              Start an intentional 1-to-1 connection.
            </p>
          )}
        </DialogHeader>

        <div className="py-4">
          {/* Step 1: Select User */}
          {step === "select" && (
            <div className="space-y-4">
              <div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by username or choose from matches…"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2 px-1">
                  You can only open one Portal per person.
                </p>
              </div>

              {searching && (
                <div className="py-8 text-center text-muted-foreground">
                  Searching...
                </div>
              )}

              {!searching && searchResults.length === 0 && searchQuery.length >= 2 && (
                <div className="py-8 text-center text-muted-foreground">
                  No users found
                </div>
              )}

              {!searching && searchResults.length > 0 && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {searchResults.map((result) => (
                    <button
                      key={result.id}
                      onClick={() => handleSelectUser(result)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted transition-colors"
                    >
                      <Avatar>
                        <AvatarImage src={result.photo_url || undefined} />
                        <AvatarFallback>{result.name?.[0] || "?"}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{result.name}</span>
                    </button>
                  ))}
                </div>
              )}

              {searchQuery.length < 2 && !searching && (
                <>
                  {/* Suggested Matches */}
                  {suggestedMatches.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium">Suggested</span>
                      </div>
                      <div className="space-y-2">
                        {suggestedMatches.map((match) => (
                          <button
                            key={match.id}
                            onClick={() => handleSelectUser(match)}
                            className="w-full flex items-center justify-between p-3 rounded-xl border border-border/50 hover:border-primary/30 hover:bg-muted/50 transition-all"
                          >
                            <div className="flex items-center gap-3">
                              <Avatar className="w-10 h-10">
                                <AvatarImage src={match.photo_url || undefined} />
                                <AvatarFallback>{match.name?.[0] || "?"}</AvatarFallback>
                              </Avatar>
                              <span className="font-medium">{match.name}</span>
                            </div>
                            <span className="text-xs text-primary font-medium">Open Portal</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty State */}
                  {suggestedMatches.length === 0 && (
                    <div className="py-8 text-center">
                      <User className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                      <p className="font-medium text-sm mb-1">
                        Choose someone you want to connect with intentionally.
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Portals start with purpose — not small talk.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 2: Select Mode */}
          {step === "mode" && (
            <div className="space-y-3">
              {selectedUser && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 mb-4">
                  <Avatar>
                    <AvatarImage src={selectedUser.photo_url || undefined} />
                    <AvatarFallback>{selectedUser.name?.[0] || "?"}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{selectedUser.name}</p>
                    <p className="text-xs text-muted-foreground">Portal recipient</p>
                  </div>
                </div>
              )}

              <p className="text-sm text-muted-foreground mb-2 text-center">
                Choose the energy for this connection
              </p>

              {modes.map((mode) => {
                const Icon = mode.icon;
                const isSelected = selectedMode === mode.id;
                return (
                  <button
                    key={mode.id}
                    onClick={() => handleSelectMode(mode.id)}
                    className={cn(
                      "w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all relative overflow-hidden",
                      isSelected
                        ? `border-transparent ${mode.gradient} text-white shadow-lg`
                        : "border-border hover:border-primary/50 hover:bg-muted/50"
                    )}
                  >
                    {/* Aura glow effect when selected */}
                    {isSelected && (
                      <div className={cn(
                        "absolute inset-0 bg-gradient-to-r opacity-20 animate-pulse",
                        mode.auraColor
                      )} />
                    )}
                    
                    <div className={cn(
                      "p-2 rounded-lg relative z-10",
                      isSelected 
                        ? "bg-white/20" 
                        : `bg-gradient-to-br ${mode.bgGradient}`
                    )}>
                      <Icon className={cn(
                        "w-5 h-5 relative z-10",
                        isSelected ? "text-white" : "text-primary"
                      )} />
                    </div>
                    <div className="text-left relative z-10">
                      <p className={cn(
                        "font-medium",
                        isSelected && "text-white"
                      )}>{mode.label}</p>
                      <p className={cn(
                        "text-sm",
                        isSelected ? "text-white/80" : "text-muted-foreground"
                      )}>{mode.description}</p>
                    </div>
                    
                    {isSelected && (
                      <div className="ml-auto relative z-10">
                        <div className={cn(
                          "w-3 h-3 rounded-full animate-pulse",
                          `bg-gradient-to-r ${mode.auraColor}`
                        )} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Step 3: Add Intro Capsule (Optional) */}
          {step === "intro" && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                <Avatar>
                  <AvatarImage src={selectedUser?.photo_url || undefined} />
                  <AvatarFallback>{selectedUser?.name?.[0] || "?"}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{selectedUser?.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {modes.find(m => m.id === selectedMode)?.label}
                  </p>
                </div>
              </div>

              <IntroCapsuleRecorder
                onIntroReady={handleIntroReady}
                onClear={handleIntroClear}
                currentIntro={introCapsule}
                mode={selectedMode}
              />

              {/* Intro status indicator */}
              {introCapsule && (
                <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/10">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-700">Intro ready!</p>
                    <p className="text-xs text-green-600/70 flex items-center gap-1">
                      {getIntroTypeIcon()}
                      {introCapsule.type === "video" && "Video intro attached"}
                      {introCapsule.type === "voice" && "Voice intro attached"}
                      {introCapsule.type === "text" && "Text intro attached"}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Add Message */}
          {step === "message" && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                <Avatar>
                  <AvatarImage src={selectedUser?.photo_url || undefined} />
                  <AvatarFallback>{selectedUser?.name?.[0] || "?"}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-medium">{selectedUser?.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {modes.find(m => m.id === selectedMode)?.label}
                  </p>
                </div>
                {introCapsule && (
                  <div className="p-1.5 rounded-lg bg-green-500/10">
                    {getIntroTypeIcon()}
                  </div>
                )}
              </div>

              <Textarea
                placeholder="Add a personal message (optional)..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground text-right">
                {message.length}/500
              </p>
            </div>
          )}
        </div>

        {/* Safety Nudge */}
        <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground/70 pb-2">
          <Shield className="w-3 h-3" />
          <span>You can close a Portal anytime.</span>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          {step !== "select" && (
            <Button variant="outline" onClick={handleBack} className="flex-1">
              Back
            </Button>
          )}
          {step === "intro" && (
            <Button
              onClick={introCapsule ? handleContinueWithIntro : handleSkipIntro}
              className="flex-1 bg-primary hover:bg-primary/90"
            >
              {introCapsule ? "Continue" : "Skip for now"}
            </Button>
          )}
          {step === "message" && (
            <Button
              onClick={handleSend}
              disabled={sending}
              className="flex-1 bg-primary hover:bg-primary/90"
            >
              {sending ? "Sending..." : "Send Invite"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePortalModal;
